import {
  INTL_DEVICE_LABELS,
  INTL_REQUEST_MAX_QUANTITY,
  type IntlAllLine,
  type IntlDeviceModel,
} from '@staff-app/shared-types';

/** One row of the new-request form: a model picked (or not yet) and the quantity as typed. */
export interface FormLine {
  deviceModel: IntlDeviceModel | '';
  quantity: string;
}

/**
 * Every reason the device lines cannot be filed; empty = valid. One line per model (the table's
 * primary key is request + model, so a second Pro line would be refused — the rep adds to the first),
 * a whole quantity between 1 and the cap on each, and at least one line.
 */
export function formLineProblems(lines: readonly FormLine[]): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  if (!lines.length) problems.push('Add at least one device.');
  lines.forEach((l, i) => {
    const n = i + 1;
    if (!l.deviceModel) {
      problems.push(`Line ${n}: pick a device.`);
      return;
    }
    const label = INTL_DEVICE_LABELS[l.deviceModel] ?? l.deviceModel;
    if (seen.has(l.deviceModel)) problems.push(`${label} is listed twice — put the whole quantity on one line.`);
    seen.add(l.deviceModel);
    const q = Number(l.quantity);
    if (!l.quantity.trim() || !Number.isInteger(q) || q < 1 || q > INTL_REQUEST_MAX_QUANTITY) {
      problems.push(`${label}: quantity must be a whole number from 1 to ${INTL_REQUEST_MAX_QUANTITY}.`);
    }
  });
  return problems;
}

/** The lines as the mutation takes them. Call only when `formLineProblems` is empty. */
export function toSubmitLines(lines: readonly FormLine[]): IntlAllLine[] {
  return lines.map((l) => ({ deviceModel: l.deviceModel as IntlDeviceModel, quantity: Number(l.quantity) }));
}
